import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import type { InnerIterateTree, ITreeNode } from "app/pages/sales-intelligence/services/SelectableTreeService";
import selectableTreeService from "app/pages/sales-intelligence/services/SelectableTreeService";

type IterateTree<T extends ITreeNode = ITreeNode> = (renderItem: InnerIterateTree<T>) => ReactNode[];

interface IResult<T extends ITreeNode = ITreeNode> {
    tree: T[];
    onSelect: (itemId: string) => void;
    allSelectedItems: Record<string, true>;
    iterateTree: IterateTree<T>;
    setTree: (tree: T[]) => void;
    setSelected: (selectedIds?: string[]) => void;
    getUpdatedSelectionById: (tree: T[], itemId: string) => T[];
    getSelectedList: (tree: T[]) => Record<string, true>;
}

export function useSelectableTree<T extends ITreeNode = ITreeNode>(
    initialTree: T[] | (() => T[]),
    initialSelectedIds?: string[] | (() => string[]),
): IResult<T> {
    // tree state
    const [tree, setTree] = useState<T[]>(() => {
        const initialTreeState = typeof initialTree === "function" ? initialTree() : initialTree;

        if (initialSelectedIds) {
            const ids = typeof initialSelectedIds === "function" ? initialSelectedIds() : initialSelectedIds;

            return selectableTreeService.updateTree(selectableTreeService.initializeTreeState(initialTreeState), ids);
        }

        return selectableTreeService.initializeTreeState(initialTreeState);
    });

    // onSelect
    const getUpdatedSelectionById = useCallback((currentTree: T[], itemId: string): T[] => {
        return selectableTreeService.getUpdatedSelectionById(currentTree, itemId);
    }, []);

    const onSelect = useCallback(
        (itemId: string) => {
            setTree((prev) => getUpdatedSelectionById(prev, itemId));
        },
        [setTree],
    );

    // setSelected - select bunch of items
    const setSelected = useCallback((selectedIds?: string[]) => {
        const ids = selectedIds ?? [];

        setTree((prevTree) => selectableTreeService.updateTree(prevTree, ids));
    }, []);

    // recursive render of tree
    const iterateTree = useCallback(
        (renderItem: InnerIterateTree<T>): ReactNode[] => selectableTreeService.iterateTree(renderItem, tree),
        [tree],
    );

    const getSelectedList = useCallback((currentTree: T[]) => selectableTreeService.getSelectedList(currentTree), []);

    const allSelectedItems = useMemo(() => selectableTreeService.getAllSelectedItems(tree), [tree]);

    return {
        tree,
        allSelectedItems,
        onSelect,
        iterateTree,
        setTree,
        setSelected,
        getUpdatedSelectionById,
        getSelectedList,
    };
}
